import {styled} from '@volkswagen-onehub/components-core';
import React, {useState, useRef, useContext, useEffect} from 'react';
import {Stage, Layer, Line } from 'react-konva';
import {ResponseVehicle} from '../../../types/api';
import Konva from 'konva';
import ImageLayer from './ImageLayer';
import Caret from './Caret';
import useImagesLayer from '../../../hooks/use-images-layer';
import useImagesUpdateWidth from '../../../hooks/use-images-update-width';
import useImagesGetSize from '../../../hooks/use-images-get-size';
import {changeCursor} from '../../../utils/app.utils';
import useImagesRender from '../../../hooks/use-images-render';
import useTrans from '../../../hooks/use-trans';
import {VisualContext} from '../../../context/modules/dimensions/VisualContext';
import {Unit} from '../../../types/enums/unit.enum';
import TextLine from './TextLine';
import TextLineBackground from './TextLineBackground';

type TooltipType = {visible: boolean; y: number};

const STAGE_CSS_WIDTH = '100%';
const ADDITIONAL_LINEHEIGHT = 20;
const ADDTIONAL_TOOLTIP_HEIGHT = 20;
const ADDITIONAL_STAGE_HEIGHT = ADDITIONAL_LINEHEIGHT + ADDTIONAL_TOOLTIP_HEIGHT;
const TOP_TO_BUZZ_TOP_HEIGHT_PERCENTAGE = 5/670;
const TOP_TO_WHEEL_HEIGHT_PERCENTAGE = 658/670;
const CARET_SPACE = 6;
const STAGE_HEIGHT_MMS = 1935/(653/658);
const MM_TO_FEET: number = 0.00328084;


const AnnouncementContainer = styled.div`
  position: relative;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
`;

const StageRefContainer = styled.div`
  width: 100%;
  margin: '20px auto';
  position: 'relative';
  &:focus-visible {
    outline: 0;
    box-shadow: ${(props) => props.theme.colors.focus.main} 0px 0px 0px 3px;
  }
`;

const calculateBarYPositionInches = (
  adjustedStageHeight: number,
  reversedHeight: number
): number => {
  let adjustedHeight = adjustedStageHeight;
  let barYPositionInches = (STAGE_HEIGHT_MMS * (adjustedHeight - reversedHeight)) / adjustedHeight / 25.4;
  return barYPositionInches;
};

export const toFixed = (value: number) => {
  return value > 0 ? value.toFixed(2) : value.toFixed();
};

type MmFtType = {
  mm: number;
  ft: number;
};

const calculateDimension = (value: number): {[key in Unit]: number} => {
  const INCH_TO_MM: number = 25.4;
  const INCH_TO_FEET: number = 0.0833333;

  const mm = parseFloat(toFixed(value * INCH_TO_MM));
  const ft = parseFloat(toFixed(value * INCH_TO_FEET));

  return {
    mm,
    ft
  };
};

const Visual: React.FC<{
  vehicles: ResponseVehicle[];
}> = ({vehicles}) => {
  const visualCtx = useContext(VisualContext);

  const [announcement, setAnnouncement] = useState<string | null>(null);
  const [mmFt, setMmFt] = useState<MmFtType>({
    mm: STAGE_HEIGHT_MMS,
    ft: parseFloat(toFixed(STAGE_HEIGHT_MMS * MM_TO_FEET))
  });

  const {
    sections: {
      dimensions: {tooltip: tooltipCopy, tooltipComparison}
    },
    aria: {dimensions}
  } = useTrans();
  const [img1, img2, img3] = useImagesRender(vehicles);
  const [reversedHeight, setReversedHeight] = useState<number>(0);

  const [tooltip, setTooltip] = useState<TooltipType>({
    visible: false,
    y: 0
  });

  const stageRef = useRef<HTMLDivElement | null>(null);
  const lineRef = useRef<Konva.Line | null>(null);
  const textLineRef = useRef<Konva.Text | null>(null);
  const textTooltipRef = useRef<Konva.Text | null>(null);
  const textLineBgRef = useRef<Konva.Rect | null>(null);
  const textTooltipBgRef = useRef<Konva.Rect | null>(null);

  const stageSize = useImagesUpdateWidth(stageRef);

  const imagesLayer = useImagesLayer(
    vehicles,
    img1,
    img2,
    img3,
  );

  const {imageSizes, adjustedStageHeight} = useImagesGetSize(
    stageSize,
    img1,
    img2,
    img3
  );

  const updatePosition = () => {
    let copy: string = dimensions.announcement;

    if (mmFt?.mm) {
      copy = copy.replace('%%mm%%', Math.round(mmFt?.mm).toString());
    }

    if (mmFt?.ft) {
      copy = copy.replace('%%ft%%', mmFt.ft.toString());
    }

    setAnnouncement(copy);
  };

  useEffect(() => {
    lineRef.current?.moveToTop();
    textLineBgRef.current?.moveToTop();
    textTooltipBgRef.current?.moveToTop();
    textLineRef.current?.moveToTop();
    textTooltipRef.current?.moveToTop();
  }, [vehicles, visualCtx?.vehicle, mmFt]);

  return (
    <>
      <AnnouncementContainer aria-live="assertive">
        {announcement}
      </AnnouncementContainer>
      <StageRefContainer
        tabIndex={0}
        aria-label={tooltipComparison}
        role="img"
        ref={stageRef}
        onKeyUp={(event) => {
          if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            updatePosition();
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
          }

          setReversedHeight((prev) => {
            let newY = prev;
            if (event.key === 'ArrowUp') newY = Math.max(prev - 1, 0);
            if (event.key === 'ArrowDown') newY = prev + 1;

            const newYValue = Math.max(0, Math.min(newY, adjustedStageHeight));

            const barYPositionInches = calculateBarYPositionInches(
              adjustedStageHeight * TOP_TO_WHEEL_HEIGHT_PERCENTAGE,
              newYValue
            );

            setMmFt(() => calculateDimension(barYPositionInches));

            return Math.max(0, Math.min(newY, adjustedStageHeight));
          });
        }}
        onFocus={() => {
          setTooltip({
            visible: true,
            y: reversedHeight
          });
        }}
        onBlur={() => setTooltip({visible: false, y: 0})}
      >
        <Stage
          id="dimension-stage"
          width={stageSize.width}
          height={adjustedStageHeight + ADDITIONAL_STAGE_HEIGHT}
          style={{
            margin: '0 auto',
            width: STAGE_CSS_WIDTH
          }}
        >
          {/* Images */}
          <Layer>
            {imagesLayer?.length
              ? imagesLayer.map((imageLayer) => {
                  const imageLayerIndex = Number(imageLayer.img!.id);

                  return (
                    <ImageLayer
                      key={'image-layer-' + imageLayerIndex}
                      {...imageLayer}
                      x={stageSize.width / 2 - imageSizes[imageLayerIndex].width / 2}
                      y={adjustedStageHeight + 20 - imageSizes[imageLayerIndex].height}
                      // overwrite width and height during window resize
                      width={imageSizes[imageLayerIndex].width as unknown as number}
                      height={imageSizes[imageLayerIndex].height}
                    />
                  );
                })
              : null}

          </Layer>

          {/* Guides */}
          <Layer opacity={0}>
            <Line
              points={[
                0,
                ADDITIONAL_LINEHEIGHT,
                stageSize.width,
                ADDITIONAL_LINEHEIGHT
              ]}
              y={(adjustedStageHeight*TOP_TO_WHEEL_HEIGHT_PERCENTAGE)}
              width={stageSize.width}
              stroke="green"
              strokeWidth={1}
              hitStrokeWidth={30}
            />

            <Line
              points={[
                0,
                ADDITIONAL_LINEHEIGHT,
                stageSize.width,
                ADDITIONAL_LINEHEIGHT
              ]}
              y={(adjustedStageHeight*TOP_TO_BUZZ_TOP_HEIGHT_PERCENTAGE)}
              width={stageSize.width}
              stroke="orange"
              strokeWidth={1}
              hitStrokeWidth={30}
            />

            <Line
              points={[
                0,
                ADDITIONAL_LINEHEIGHT,
                stageSize.width,
                ADDITIONAL_LINEHEIGHT
              ]}
              y={0}
              width={stageSize.width}
              stroke="purple"
              strokeWidth={1}
              hitStrokeWidth={30}
            />

            <Line
              points={[
                0,
                ADDITIONAL_LINEHEIGHT,
                stageSize.width,
                ADDITIONAL_LINEHEIGHT
              ]}
              y={adjustedStageHeight}
              width={stageSize.width}
              stroke="cyan"
              strokeWidth={1}
              hitStrokeWidth={30}
            />
          </Layer>

          {/* Overlay items */}
          <Layer
            x={0}
            y={0}
            width={stageSize.width}
            height={adjustedStageHeight + ADDITIONAL_LINEHEIGHT}
            stroke="pink"
            strokeWidth={10}
          >
            <TextLineBackground
              ref={textLineBgRef}
              y={reversedHeight}
              width={textLineRef.current ? textLineRef.current?.textWidth : 0}
            />

            <TextLine
              y={reversedHeight}
              ref={textLineRef}
              copy={Math.round(mmFt?.mm) + 'mm = ' + mmFt?.ft + 'ft'}
            />

            {tooltip.visible && window.innerWidth > 560 ? (
              <>
                <TextLineBackground
                  ref={textTooltipBgRef}
                  y={reversedHeight + (ADDTIONAL_TOOLTIP_HEIGHT + 2)}
                  width={
                    textTooltipRef.current ? textTooltipRef.current.textWidth : 0
                  }
                />

                <TextLine
                  ref={textTooltipRef}
                  y={reversedHeight + ADDTIONAL_TOOLTIP_HEIGHT}
                  copy={tooltipCopy}
                />
              </>
            ) : null}

            <Line
              points={[
                0,
                ADDITIONAL_LINEHEIGHT,
                stageSize.width,
                ADDITIONAL_LINEHEIGHT
              ]}
              y={reversedHeight}
              stroke="grey"
              strokeWidth={0.3}
              hitStrokeWidth={30}
              draggable
              onDragStart={() => {changeCursor('grabbing')}}
              onDragEnd={() => {updatePosition()}}
              dragBoundFunc={(pos) => {
                const reversedHeightValue = Math.max(
                  0,
                  Math.min(adjustedStageHeight * TOP_TO_WHEEL_HEIGHT_PERCENTAGE, pos.y)
                );

                setReversedHeight(reversedHeightValue);

                setTooltip({
                  visible: true,
                  y: reversedHeightValue + ADDTIONAL_TOOLTIP_HEIGHT
                });

                const barYPositionInches = calculateBarYPositionInches(
                  adjustedStageHeight * TOP_TO_WHEEL_HEIGHT_PERCENTAGE,
                  reversedHeight
                );

                setMmFt(() => calculateDimension(barYPositionInches));

                return {
                  x: 0,
                  y: Math.max(0, Math.min(adjustedStageHeight * TOP_TO_WHEEL_HEIGHT_PERCENTAGE, pos.y))
                };
              }}
              ref={lineRef}
              onMouseEnter={changeCursor('grab')}
              onMouseMove={() => {
                setTooltip({
                  visible: true,
                  y: reversedHeight + ADDTIONAL_TOOLTIP_HEIGHT
                });
              }}
              onMouseLeave={(e) => {
                const stage = e.target.getStage();

                if (stage && stage.container()) {
                  stage.container().style.cursor = 'DEFAULT';
                }

                setTooltip({visible: false, y: 0});
              }}
            />

            {/* Upward */}
            <Caret
              x={stageSize.width - 7}
              y={reversedHeight + (ADDITIONAL_LINEHEIGHT - CARET_SPACE)}
            />

            {/* Downward */}
            <Caret
              rotation={180}
              x={stageSize.width - 7}
              y={reversedHeight + ADDITIONAL_LINEHEIGHT + CARET_SPACE}
            />
          </Layer>

        </Stage>
      </StageRefContainer>
    </>
  );
};

export default Visual;
